import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenu, MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { FuseHorizontalNavigationBasicItemComponent } from '@fuse/components/navigation/horizontal/components/basic/basic.component';
import { FuseHorizontalNavigationDividerItemComponent } from '@fuse/components/navigation/horizontal/components/divider/divider.component';
import { FuseHorizontalNavigationComponent } from '@fuse/components/navigation/horizontal/horizontal.component';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { ModuleDropdownShortcutService } from 'app/shared/services/module-dropdown-shortcut.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'fuse-horizontal-navigation-branch-item',
    templateUrl: './branch.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        MatMenuModule,
        NgTemplateOutlet,
        FuseHorizontalNavigationBasicItemComponent,
        forwardRef(() => FuseHorizontalNavigationBranchItemComponent),
        FuseHorizontalNavigationDividerItemComponent,
        MatTooltipModule,
        MatIconModule,
    ],
})
export class FuseHorizontalNavigationBranchItemComponent
    implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_child: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _moduleDropdownShortcuts = inject(ModuleDropdownShortcutService);
    private _router = inject(Router);

    @Input() child: boolean = false;
    @Input() item: FuseNavigationItem;
    @Input() name: string;
    @ViewChild('matMenu', { static: true }) matMenu: MatMenu;
    @ViewChild('trigger', { static: false }) menuTrigger: MatMenuTrigger;

    private _fuseHorizontalNavigationComponent: FuseHorizontalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /** True when any descendant link matches the current route (e.g. Transactions when on /sales/add) */
    hasActiveChild = false;

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Get the parent navigation component
        this._fuseHorizontalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // Subscribe to onRefreshed on the navigation component
        this._fuseHorizontalNavigationComponent.onRefreshed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        // Subscribe to NavigationEnd events to close menu when navigation completes
        // and to update hasActiveChild so parent (Masters/Transactions) shows blue when on child route
        this._router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((event: NavigationEnd) => {
                this.closeMenu();
                this._updateHasActiveChild(event.urlAfterRedirects || this._router.url);
            });

        this._updateHasActiveChild(this._router.url);

        // Subscribe to module dropdown shortcuts (Alt+M, Alt+T, etc.)
        this._moduleDropdownShortcuts.openDropdown$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((moduleId) => {
                if (!this.child) {
                    if (this.item?.id === moduleId) {
                        setTimeout(() => {
                            if (this.menuTrigger && !this.menuTrigger.menuOpen) {
                                this.menuTrigger.openMenu();
                                this._changeDetectorRef.markForCheck();
                            }
                        }, 0);
                    } else {
                        // Close when a different module shortcut is pressed
                        this.closeMenu();
                    }
                }
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Trigger the change detection
     */
    triggerChangeDetection(): void {
        // Mark for check
        this._changeDetectorRef.markForCheck();
    }

    /**
     * Close the menu programmatically
     * This method closes the menu and can be called from click handlers or navigation events
     * Works for all menu levels (top-level and nested)
     */
    closeMenu(): void {
        // Only close if this is not a child menu and trigger exists and is open
        if (!this.child && this.menuTrigger && this.menuTrigger.menuOpen) {
            this.menuTrigger.closeMenu();
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Handle Enter key on a menu item (e.g. after arrowing down to Doctors, Suppliers).
     * Ensures Enter always closes the menu and navigates for items with a link.
     */
    onMenuItemEnter(event: KeyboardEvent, menuItem?: FuseNavigationItem): void {
        if (!menuItem?.link || menuItem.externalLink || menuItem.disabled) {
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        this.closeMenu();
        this._router.navigate([menuItem.link], {
            fragment: menuItem.fragment ?? undefined,
            queryParams: menuItem.queryParams ?? undefined,
            queryParamsHandling: menuItem.queryParamsHandling ?? undefined,
        });
    }

    /**
     * Handle menu item click - closes menu and navigates when activated by keyboard (Enter).
     * When Enter is pressed, Material may trigger click on a child element; onMenuItemEnter handles
     * Enter explicitly, and this handles click (mouse or when target is the container).
     */
    onMenuItemClick(event?: Event, menuItem?: FuseNavigationItem): void {
        if (event && menuItem?.link && !menuItem.externalLink) {
            event.preventDefault();
            event.stopPropagation();
        }
        this.closeMenu();
        if (menuItem?.link && !menuItem.externalLink) {
            this._router.navigate([menuItem.link], {
                fragment: menuItem.fragment ?? undefined,
                queryParams: menuItem.queryParams ?? undefined,
                queryParamsHandling: menuItem.queryParamsHandling ?? undefined,
            });
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update hasActiveChild: true when any descendant link matches the current URL.
     * Ensures parent (Masters, Transactions, etc.) shows blue when on a child page.
     *
     * @see https://angular.dev/api/router/Router#isActive
     */
    private _updateHasActiveChild(currentUrl: string): void {
        if (this.item) {
            this.hasActiveChild = this._hasActiveChild(this.item, currentUrl);
            this._changeDetectorRef.markForCheck();
        }
    }

    /**
     * Check if any descendant has a link that matches the current URL.
     * Uses Router.isActive when possible; falls back to path prefix matching.
     *
     * @param item Navigation item (with optional children)
     * @param currentUrl Current router URL (e.g. /sales/add)
     */
    private _hasActiveChild(
        item: FuseNavigationItem,
        currentUrl: string
    ): boolean {
        const children = item.children;

        if (!children) {
            return false;
        }

        for (const child of children) {
            if (child.children && this._hasActiveChild(child, currentUrl)) {
                return true;
            }
            if (child.link) {
                const link =
                    typeof child.link === 'string'
                        ? child.link.startsWith('/')
                            ? child.link
                            : '/' + child.link
                        : null;
                if (
                    link &&
                    (currentUrl === link ||
                        currentUrl.startsWith(link + '/') ||
                        (link !== '/' && currentUrl.startsWith(link)))
                ) {
                    return true;
                }
                if (
                    this._router.isActive(
                        child.link as string,
                        child.exactMatch ?? false
                    )
                ) {
                    return true;
                }
            }
        }

        return false;
    }
}
